// What a sentence typed into the command bar is asking for. Kept apart from
// the bar itself, which needs a browser, so it can be tested on its own.
//
// It reads a sentence from its verb, the way a person would. "Make a widget
// that sorts my links" makes a widget, though it says sort; "organize my board
// and put the revenue chart first" organizes the board, though it names a
// chart; "make my clock bigger" sizes a clock that is there, and "one of my
// charts is broken" is about one that is there: what is already on the board,
// "my clock" or "the charts", is never made new. "I want to" and "we need to"
// are read by the verb after them. Look, style and background make a theme
// only when no widget is named, and sorting or tidying inside one widget is a
// question for the agent, not the board's layout.
//
// The agent gets what was typed. Only a bare description ("snake game", "a
// forest theme") gets "Make a widget:" or "Make a theme:" in front of it, and
// it, or a widget or theme asked for with a verb, loses only "hey, can you"
// before it, never a word of its name ("now playing", "Hello Kitty"). A
// description is a name with the word for its kind last, and at most what it
// is like after it ("with neon colors", "that shows my mrr", "for London"); a
// look for a new widget is one too ("a retro look for a clock"). Anything else
// said of it makes it a statement, which goes as typed, whatever word it comes
// after: "snake game too small", "a widget disappeared", "clock on my board is
// an hour behind", "a widget that never loads", "chart with no data". So does
// a whole dashboard, which is many widgets, or "two clocks": not one.
//
// Only the start of a long text is read, once, word by word, so a pasted page
// costs what a sentence does.

export type Intent = "widget" | "theme" | "arrange" | "ask" | null;

/** How much of a text is read. A request is a sentence or two; past this, its start says what it is. */
const READ = 320;

const WORD = /[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*|[,.;:!?]/gu;
const MARK = /^[,.;:!?]$/;
const words = (s: string) => new Set(s.split(" "));

/** Words before the request itself: "hey", "please", "so". */
const LEAD = words("hey hi hello ok okay so now and also then please pls plz kindly just maybe um uh oh well alright right yo");
/** Of those, the ones that never start a name: "please make...", where "now playing widget" and "Hello Kitty theme" keep theirs. */
const FILLER = words("please pls plz kindly um uh");
/** How a request may be done, before its verb: "automatically arrange my widgets". */
const MANNER = words("auto automatically quickly simply really properly neatly nicely actually");
/** A thank-you at the end. */
const POLITE = words("please pls plz thanks thx ty");
const MODAL = words("can could would will");
/** A question goes to the agent as it was asked. */
const QUESTION = words("what what's whats why how how's when where where's who who's whose which is are was were does do did has have had should shall am isn't aren't doesn't don't didn't can't won't will would could can may might");

/** Verbs that make a new thing, and verbs that put one on the board. */
const CREATE = words("make build create design draw generate craft whip compose produce sketch code");
const PLACE = words("add insert put place drop pop stick");
/** Verbs that tidy, and verbs that move or resize. */
const TIDY = words("organize organise reorganize reorganise arrange rearrange group regroup sort order reorder structure restructure categorize categorise tidy neaten declutter de-clutter unclutter clean lay layout align line stack space spread straighten split distribute cluster even");
const MOVE = words("move put place position bring shift drag swap push relocate pin dock keep");
const RESIZE = words("resize shrink enlarge widen grow expand stretch scale");
/** Verbs that draw numbers as a widget, when they are verbs: "chart my weight", "plot signups over time". */
const PLOT = words("chart graph plot map visualize visualise");
/** Verbs that change, read or use what is there: the agent gets the sentence as it is. */
const CHANGE = words("fix update change edit rename remove delete hide unhide refresh reload duplicate copy lock unlock recolor recolour colour color reset clear restore undo redo improve tweak adjust repair debug explain describe summarize summarise read check open close start stop pause resume play set turn switch use apply wear try connect disconnect link embed import export save load share publish send post tell find search look see view compare calculate compute measure convert translate pull fetch sync plan write draft enable disable mute unmute replace merge combine archive empty track list time count log note schedule tally record monitor watch follow get let go do");

/** Words that can name a thing as well as do one: "line chart", "space theme", "list of my todos", "clean minimal theme". */
const NOUNISH = words("group order structure layout line stack space split spread place position pin dock drop pop stick insert design sketch code draw clean tidy neat color colour list track time count log note schedule tally record plan look set play post chart graph plot map display");
/** Any verb the board knows: where one request ends and the next begins. */
const VERB = new Set([...CREATE, ...PLACE, ...TIDY, ...MOVE, ...RESIZE, ...PLOT, ...CHANGE, "give", "show", "display", "make"]);
/** The particles a tidying or moving verb takes: "tidy up", "lay out", "space them out". */
const PARTICLE = words("up out together around away apart back over down neatly nicely properly evenly");
/** What is already there, or a new one. */
const DEFINITE = words("the this that these those my our your its their his her both each every all");
const NEWLY = words("a an another new some one two three four five six several few more");
const PRONOUN = words("it them this that these those everything everyone");
/** Words that end the thing a verb acts on: what follows says where, how, or what for. */
const ENDS = words(
  "of for with in on at to from by about like into onto over under above below beside besides between near around through per via as after before until without within across behind inside than that which who whose where when while because since so and or but then if showing shows show called named saying says i you we they he she me us them is are was were be been being isn't aren't wasn't weren't keep keeps kept has have had hasn't haven't should would could can will must might may shall do does did don't doesn't didn't won't can't cannot please now too again first last together up out back down away off here there looks looking seem seems overlap overlaps overlapping need needs want wants go goes went got gets appear appears appearing stopped broke broken crashed",
);
/** "One of my charts", "some of the widgets": ones that are there. */
const QUANTITY = words("one two three some most all none each many any both half few several either neither");

/** What the board, or many of its widgets, are called. */
const BOARD = words("board boards layout dashboard dashboards canvas workspace page screen space desk desktop");
const MANY = words("widgets tiles cards charts graphs plots timers clocks counters trackers gauges meters panels items blocks sections headings groups stickies calendars countdowns games feeds maps metrics goals kpis stats embeds images frames players boxes windows things stuff everything all mess rest");
/** The board is named somewhere in the sentence: "it" can be the board. */
const BOARD_SAID = words("board boards layout dashboard canvas workspace widgets tiles cards charts graphs sections");
/** What a widget is called. A dashboard is not one: it is a board of them. */
const WIDGET = words(
  "widget widgets chart charts graph graphs plot plots tracker trackers counter counters timer timers gauge gauges meter meters heatmap heatmaps funnel funnels donut donuts pie pies bar bars leaderboard leaderboards scoreboard scoreboards sparkline sparklines countdown countdowns clock clocks stopwatch stopwatches pomodoro pomodoros calendar calendars kanban kanbans visualization visualizations visualisation visualisations visualizer visualiser tile tiles thermometer thermometers game games list lists checklist checklists todo todos to-do card cards note notes sticky stickies memo memos feed feeds player players frame frames table tables map maps metric metrics kpi kpis panel panels goal goals ticker tickers quote quotes poll polls calculator calculators converter converters planner planners journal journals log logs",
);
/** What may follow the name of a thing to make: what it is like, what it is for, what it shows. "Shows" is not one: "the calendar shows the wrong week" says something of one. */
const CONNECT = words(
  "that which who whose where when with without for of about like in on at to from by into onto as via until till showing displaying tracking counting listing plotting charting comparing featuring using called named titled saying inspired based styled themed",
);
/** What the name of a thing to make may end in, past the word for its kind: "a todo list app", "a snake game clone", "a kanban board". */
const GENERIC = words("app apps tool tools thing things view display version clone replica edition face board");
/** Words that say something is wrong with a widget, not what a new one is like: "a widget that does not load", "the clock in the wrong place", "a widget that never loads", "chart showing NaN". */
const FAULT = words(
  "not isn't aren't wasn't weren't doesn't don't didn't won't can't cannot couldn't wouldn't hasn't haven't never broken broke breaks breaking crash crashes crashed crashing stuck frozen froze freezes freezing glitch glitches glitchy glitching buggy bugged wrong missing gone disappear disappears disappeared disappearing vanish vanishes vanished vanishing overflow overflows overflowing flicker flickers flickered flickering stall stalls stalled stalling stopped failed died hangs hanging stutters stuttering lag lags lagging laggy fails failing too cut empty blank nothing forever slow slowly sluggish behind delayed outdated stale upside nan undefined null",
);
/** What a widget is about: past these, a state it can list ("a counter of broken links") is what it shows, not what is wrong with it. */
const CONTENT = words("of for about");
/** The states things on a widget can be in, as against what is wrong with one: "broken links", "missing invoices", "stuck deals", "failed payments". */
const STATES = words("broken missing failing crash crashes stuck frozen stalled stopped failed lagging slow behind delayed outdated stale empty blank");
/** A verb of the sentence's own, past where a widget is or what it is for: "the clock on my board is an hour behind", "the chart for revenue keeps resetting". */
const FINITE = words(
  "is are was were isn't aren't wasn't weren't has have had hasn't haven't hadn't does do did doesn't don't didn't will won't would wouldn't can can't cannot could couldn't should shouldn't keeps keep kept seems seemed appears appeared becomes became gets got goes went it's there's",
);
/** Verbs a widget is said to do that are hardly ever nouns: one with anything after it is the sentence's verb, "the weather widget for London shows Paris". */
const DOES = words("shows says displays reads loads refreshes reloads resets restarts overlaps hides looks stays sits rings beeps buzzes blinks spins scrolls shrinks grows expands collapses opens closes forgets deletes erases removes duplicates ignores loses truncates squishes squashes wobbles shakes syncs");
/** Verbs that are often nouns or adjectives ("product updates", "my runs", "lost items"): the sentence's verb only with an object, a particle or "-ing" after them. */
const DOES_TOO = words("updates lists counts tracks runs stops starts moves works plays takes covers needs uses changes turns misses skips drops repeats wraps clips snaps sticks blocks flashes slides jumps logs times errors reset lost moved jumped started showed said displayed changed turned shrank grew took ran");
/** What the verb's object starts with when it is one: "lost my streak", "covers the notes", "moved it". */
const OBJECT = words("my our your his her its their the it them everything all");
/** Who does the verb in a clause of its own: "a widget that I made", "a list of things I have to do". */
const SUBJECT = words("i we you they he she i've we've you've they've i'd we'd");
/** What a user does to a widget on the board: "a widget that I made" is one that is there, where "a theme I saw on dribbble" is one to make. */
const BOARD_ACTS = words("made added created built put placed pinned dropped installed connected imported copied duplicated moved changed edited renamed resized arranged opened saved shared set had deleted removed lost");
/** Verbs in the past that end in no "-ed": "a timer that went off twice", "a kanban that lost my cards". */
const WENT = words("broke froze lost went got took ran fell grew shrank hid kept left made said showed stuck sent saw became began came");
/** What a widget does to itself that nobody asked for: "a timer that resets itself". */
const UNASKED = words("resets restarts reloads refreshes closes collapses shrinks resizes moves scrolls clears deletes empties hides minimizes rearranges");
/** When something happens, which makes doing it a feature: "a counter that resets itself every day". */
const SCHEDULE = words("every each daily weekly monthly hourly nightly at when after before on");
/** The words a widget shows things with: "chart showing NaN", "a widget with an error", "a counter that says zero". */
const SHOWS = words("showing shows displaying displays saying says reading reads with has had having gives giving throws throwing returns returning");
/** What a widget shows when something is wrong with it, said on its own: "widget with an error", "chart showing zero". */
const WRONG = words("error errors zero 0");
/** What a widget's content is, as it says it has none: "chart with no data", "calendar without any events". */
const CONTENTS = words("data events items entries content contents info information results values records rows posts news updates tasks cards notes stats");
/** A widget that shows the time shows a past day only when it is wrong: "clock showing yesterday's date", "calendar showing last week". */
const TIMELY = words("clock clocks calendar calendars countdown countdowns date dates");
const PAST_DAY = words("yesterday yesterday's last previous");
/** A time a widget was made at: "the chart from yesterday" is one that is there. */
const WHEN_MADE = words("yesterday today earlier before ago last this morning afternoon evening night week weekend month year time hour hours day days minute minutes few couple of a an the");
/** How many widgets, when it is more than one: "two clocks" is not one widget to make. */
const COUNT = words("two three four five six seven eight nine ten several few multiple many more some couple");
/** What a theme is called, and the words a look is described in. */
const THEME = words("theme themes");
const STYLE = words("look looks style styles background backgrounds wallpaper wallpapers vibe vibes aesthetic aesthetics skin skins palette palettes scheme schemes mood moods feel mode");
/** A layout to make is the board arranged: "a new layout for the widgets", "add headings to organize them". */
const LAYOUT = words("layout layouts arrangement arrangements rearrangement reorganization reorganisation tidy-up clean-up cleanup headings sections dividers");
/** These are a layout only when they are of the widgets or the board: "a grid of my widgets", "room for a new widget". */
const ROOM = words("grid order structure grouping room space spacing padding gap gaps heading section divider");

/** What a widget has, not what it is: "new chart colors", "a title for my calendar". */
const PROPERTY = words("color colors colour colours title titles font fonts size sizes label labels name names border borders icon icons caption captions legend legends axis axes header headers value values unit units text");
/** Where a widget is moved to. In an order, a row or a group is what arranging the board does; a side of it is a move the agent makes. */
const IN_ORDER = words("first last top bottom front start beginning next beside besides together row rows column columns grid line order sections section groups above below under underneath after before between near alongside");
const TO_SIDE = words("right left corner middle center centre side");
/** The sizes "make it bigger" asks for. */
const SIZE = words("bigger smaller wider narrower taller shorter larger huge tiny full-width");
/** The board, or its widgets, tidy: "make the board tidy", "I want my board organized". */
const NEAT = words("tidy tidier neat neater cleaner organized organised orderly decluttered uncluttered sorted grouped arranged aligned structured");

type Read = { intent: Intent; bare: boolean };
type Thing = { det: "def" | "new" | null; words: string[]; end: number; pronoun: string | null };

const ASK: Read = { intent: "ask", bare: false };
const ARRANGE: Read = { intent: "arrange", bare: false };
const reads = (intent: Intent, bare = false): Read => ({ intent, bare });

/** Word i as the verb it is: "re-arrange" is "rearrange", and "organizing" is "organize" with what it acts on after it ("organizing my board", not "moving average chart"). */
function verbAt(w: string[], i: number): string {
  let x = w[i] ?? "";
  if (VERB.has(x)) return x;
  if (x.startsWith("re-") && VERB.has("re" + x.slice(3))) return "re" + x.slice(3);
  if (/^(re|auto)-/.test(x)) x = x.replace(/^(re|auto)-/, "");
  if (VERB.has(x)) return x;
  const n = w[i + 1] ?? "";
  if (x.endsWith("ing") && (DEFINITE.has(n) || PRONOUN.has(n) || PARTICLE.has(n) || NEWLY.has(n) || n === "me" || n === "us")) {
    // "tidying", "organizing", "planning"
    const stem = x.slice(0, -3);
    if (VERB.has(stem)) return stem;
    if (VERB.has(stem + "e")) return stem + "e";
    if (stem.length > 2 && stem.at(-1) === stem.at(-2) && VERB.has(stem.slice(0, -1))) return stem.slice(0, -1);
  }
  return w[i] ?? "";
}

/** Whether a lead word is one here: "right" only as "right,". */
const leadWord = (w: string[], i: number) => LEAD.has(w[i] ?? "") && !(w[i] === "right" && w[i + 1] !== ",");

/** Whether what starts at word j shows the words before it were a lead-in, not the start of a name: a mark, a determiner, "can you", or a verb with what it acts on. */
function setApart(w: string[], j: number): boolean {
  const x = w[j];
  if (x === undefined) return false;
  if (MARK.test(x) || FILLER.has(x) || NEWLY.has(x) || DEFINITE.has(x) || PRONOUN.has(x) || x === "i" || x === "we" || x === "i'd" || x === "we'd") return true;
  if (afterLeads(w, j) > j) return true;
  const v = verbAt(w, j);
  const n = w[j + 1];
  return VERB.has(v) && n !== undefined && (!NOUNISH.has(v) || actsOn(n));
}

/** A word after one that names a thing as well, that makes it a verb: "line up", "plot the signups", "space them out". */
const actsOn = (n: string) => MARK.test(n) || PARTICLE.has(n) || DEFINITE.has(n) || NEWLY.has(n) || PRONOUN.has(n) || BOARD.has(n) || MANY.has(n) || POLITE.has(n) || n === "me" || n === "us" || n === "by" || n === "into" || n === "to";

/** Whether "that" or "which" at j starts a clause about the word before it, by the verb after it: "chart that never updates", "chart that loads slowly", "a list that I share"; not "chart that for me". */
function aboutIt(w: string[], j: number): boolean {
  if (w[j] !== "that" && w[j] !== "which" && w[j] !== "who") return false;
  const n = w[j + 1];
  return n !== undefined && (FINITE.has(n) || FAULT.has(n) || SUBJECT.has(n) || /..(?:[^s]s|ed|ly)$/.test(n) || n === "just" || n === "also" || n === "only" || n === "still" || n === "always");
}

/** Names that start with a word that can also lead into a request, as they are typed small. */
const NAMES = ["hello kitty", "hello world", "hi score", "ok boomer", "okay boomer", "ok computer", "just dance", "then and now", "now and then"].map((s) => s.split(" "));

/** Whether the lead word at p starts a name: "Hello Kitty theme", "now playing widget", "so many stars theme", or one typed as a name, "Hey Arnold theme". */
function startsName(w: string[], p: number, caps: boolean[]): boolean {
  if (NAMES.some((s) => s.every((x, k) => w[p + k] === x))) return true;
  if (w[p] === "now" && /..ing$/.test(w[p + 1] ?? "")) return true;
  if (w[p] === "so" && (w[p + 1] === "many" || w[p + 1] === "much")) return true;
  return !!caps[p] && !!caps[p + 1];
}

/**
 * The leads before word i: "hey", "please", "can you", "I'd like you to", "let's", "how about". Careful, for the
 * words handed over, given which words were typed with a capital: a greeting goes ("hey snake game"), but a name
 * that starts with a lead word keeps it ("now playing widget", "Hello Kitty theme"), unless something shows the
 * word was a lead ("now, make a clock", "ok so a forest theme").
 */
function afterLeads(w: string[], i = 0, caps?: boolean[]): number {
  for (;;) {
    const [a, b, c, d] = [w[i], w[i + 1], w[i + 2], w[i + 3]];
    if (a === undefined) return i;
    if (MARK.test(a) && a !== "?") i++;
    else if (leadWord(w, i)) {
      if (!caps || FILLER.has(a)) {
        i++;
        continue;
      }
      let j = i;
      while (leadWord(w, j) && !FILLER.has(w[j])) j++;
      if (!setApart(w, j)) for (let p = i; p < j; p++) if (startsName(w, p, caps)) return p;
      i = j;
    } else if (MODAL.has(a) && (b === "you" || b === "u")) i += 2;
    else if ((a === "i" || a === "we") && (b === "want" || b === "need") && c === "you" && d === "to") i += 4;
    else if ((a === "i'd" || a === "we'd") && (b === "like" || b === "love") && c === "you" && d === "to") i += 4;
    else if ((a === "i" || a === "we") && b === "would" && (c === "like" || c === "love") && d === "you" && w[i + 4] === "to") i += 5;
    else if (a === "help" && (b === "me" || b === "us")) i += c === "to" ? 3 : 2;
    // "help organize my board", "would you mind organizing it", "do you mind tidying up", "automatically arrange my widgets"
    else if ((a === "help" || MANNER.has(a)) && b !== undefined && VERB.has(verbAt(w, i + 1))) i++;
    else if (a === "mind" && b?.endsWith("ing")) i++;
    else if (a === "do" && (b === "you" || b === "u") && c === "mind") i += 3;
    else if (a === "let's" || a === "lets") i++;
    else if (a === "let" && b === "us") i += 2;
    else if (a === "go" && b === "ahead" && c === "and") i += 3;
    else if (a === "try" && (b === "to" || b === "and")) i += 2;
    else if ((a === "how" || a === "what") && b === "about") i += 2;
    else return i;
  }
}

/** "I want", "I'd like", "we need": a wish. With "to", the verb after it is the request. */
function wishAt(w: string[], i: number): { to: boolean; at: number } | null {
  const [a, b, c] = [w[i], w[i + 1], w[i + 2]];
  let at = -1;
  if ((a === "i" || a === "we") && (b === "want" || b === "need" || b === "wanna")) at = i + 2;
  else if ((a === "i'd" || a === "we'd") && (b === "like" || b === "love")) at = i + 2;
  else if ((a === "i" || a === "we") && b === "would" && (c === "like" || c === "love")) at = i + 3;
  if (at < 0) return null;
  if (b === "wanna") return { to: true, at };
  return w[at] === "to" ? { to: true, at: at + 1 } : { to: false, at };
}

/** The thing a verb acts on, from word i: what kind of determiner it has, its words, and where it ends. */
function thing(w: string[], i: number): Thing {
  let det: Thing["det"] = null;
  const a = w[i];
  // "it", "them", "everything"; "this" and "these" on their own, not "this board"
  if (a !== undefined && PRONOUN.has(a)) {
    const n = w[i + 1];
    const named = n !== undefined && !MARK.test(n) && !ENDS.has(n) && !PARTICLE.has(n) && !NEWLY.has(n) && !SIZE.has(n) && !NEAT.has(n) && n !== "look" && n !== "feel" && n !== "less" && n !== "more";
    if (!named || a === "it" || a === "them" || a === "everything" || a === "everyone") return { det: "def", words: [], end: i + 1, pronoun: a };
  }
  // "all the charts", "all of my widgets", "both of the clocks", "each of the tiles"
  while (w[i] !== undefined && (DEFINITE.has(w[i]) || NEWLY.has(w[i]) || w[i] === "of")) {
    if (DEFINITE.has(w[i])) det = "def";
    else if (NEWLY.has(w[i]) && det === null) det = "new";
    i++;
  }
  const out: string[] = [];
  let j = i;
  for (; j < w.length && out.length < 8; j++) {
    const x = w[j];
    // "a clean, tidy theme": a comma between the words that describe it
    if (x === ",") {
      if (out.some((y) => WIDGET.has(y) || THEME.has(y) || STYLE.has(y) || BOARD.has(y) || LAYOUT.has(y)) || ENDS.has(w[j + 1] ?? ".") || DEFINITE.has(w[j + 1] ?? "")) break;
      continue;
    }
    if (MARK.test(x) || ENDS.has(x)) break;
    if ((x === "look" || x === "feel") && w[j + 1] === "like") break;
    // "a chart tracking signups", "a theme inspired by...": what it is like starts here; "next to the chart" is where
    if (out.length && (CONNECT.has(x) || ((x === "next" || x === "close") && w[j + 1] === "to"))) break;
    if (out.length && (DEFINITE.has(x) || NEWLY.has(x))) break;
    // "make my calendar bigger", "the board look tidy", "the charts side by side", "the widgets line up":
    // after a thing that is there, what it is made into or where it goes
    if (out.length && det === "def" && (SIZE.has(x) || NEAT.has(x) || IN_ORDER.has(x) || TO_SIDE.has(x) || x === "less" || x === "more" || x === "full" || x === "look" || x === "feel" || ((TIDY.has(x) || MOVE.has(x)) && PARTICLE.has(w[j + 1] ?? "")))) break;
    out.push(x.replace(/['’]s$/, ""));
  }
  // "stack the charts vertically": how, not what
  while (out.length > 1 && /..ly$/.test(out[out.length - 1]) && !WIDGET.has(out[out.length - 1])) {
    out.pop();
    j--;
  }
  return { det, words: out, end: j, pronoun: null };
}

const head = (t: Thing) => t.words[t.words.length - 1] ?? "";
/** The board itself, or many of its widgets: "the layout", "my widgets", "these sticky notes". */
function whole(t: Thing, boardSaid: boolean | "one"): boolean {
  if (t.pronoun) return t.pronoun === "everything" || boardSaid !== "one";
  const h = head(t);
  return BOARD.has(h) || MANY.has(h) || (h === "notes" && t.words.includes("sticky"));
}
/** One widget that is there: "the revenue chart", "my clock". */
const oneWidget = (t: Thing) => t.det === "def" && !t.pronoun && WIDGET.has(head(t)) && !MANY.has(head(t)) && !(head(t) === "notes" && !t.words.includes("sticky"));

/** The words from i to where the clause ends, at most n of them. */
function clauseFrom(w: string[], i: number, n = 14): string[] {
  const out: string[] = [];
  for (let j = i; j < w.length && out.length < n; j++) {
    if (w[j] === "." || w[j] === ";" || w[j] === "!" || w[j] === "?") break;
    out.push(w[j]);
  }
  return out;
}

/** "as a calendar widget", "in a funnel", "on a map": the widget it should be shown as. */
function asWidget(w: string[], i: number): boolean {
  const c = clauseFrom(w, i);
  for (let k = 0; k < c.length; k++) {
    if (!["as", "in", "into", "on", "onto"].includes(c[k])) continue;
    const t = thing(c, k + 1);
    if (t.det !== "def" && t.words.some((x) => WIDGET.has(x))) return true;
  }
  return false;
}

/** Where the thing ends up, past where its name ends. */
function whereTo(w: string[], i: number): "order" | "side" | null {
  const c = clauseFrom(w, i);
  for (let k = 0; k < c.length; k++) {
    const x = c[k];
    // the next request's own words: "put the numbers in place and line up the people"
    if (x === "," || ((x === "and" || x === "but" || x === "then" || x === "so") && VERB.has(c[k + 1] ?? ""))) break;
    if (x === "side" && c[k + 1] === "by") return "order";
    if (TO_SIDE.has(x)) return "side";
    if (IN_ORDER.has(x)) return "order";
  }
  return null;
}

/** What a new thing is: a widget, a theme, the board laid out, a board of its own, or none of those. */
function kindOf(t: Thing, w: string[], creating: boolean): Intent | "board" | undefined {
  const h = head(t);
  if (PROPERTY.has(h)) return undefined;
  if (THEME.has(h)) return "theme";
  if (t.words.some((x) => WIDGET.has(x))) return "widget";
  if (LAYOUT.has(h)) return "arrange";
  if (ROOM.has(h)) {
    const c = clauseFrom(w, t.end, 6);
    if (c.some((x) => BOARD_SAID.has(x) || MANY.has(x) || WIDGET.has(x))) return "arrange";
  }
  if (STYLE.has(h) && !(h === "look" && w[t.end] === "at")) {
    // "a retro look for my clock", "a dark background for my big weekly revenue chart": that widget's look, for
    // the agent to change; "a retro look for a clock": a new widget with it. The board's, or its widgets', is a theme.
    const rest = clauseFrom(w, t.end, 14);
    for (let k = 0; k < rest.length; k++) {
      if (!["for", "to", "on", "of", "in", "onto", "into"].includes(rest[k])) continue;
      const o = thing(rest, k + 1);
      if (!o.words.some((x) => WIDGET.has(x) && !MANY.has(x))) continue;
      if (o.det === "def") return "ask";
      if (o.det === "new") return "widget";
    }
    return "theme";
  }
  // a board of its own; a dashboard asked to be made is the widgets on one
  if (h === "board" || h === "boards" || (!creating && (h === "dashboard" || h === "dashboards"))) return "board";
  return creating && t.words.length ? "widget" : undefined;
}

/** A new thing to make, after a verb that makes or adds: "a kanban widget", "me a forest theme", "my board a forest theme". */
function made(w: string[], i: number, creating: boolean, boardSaid: boolean | "one"): Read | undefined {
  if (w[i] === "me" || w[i] === "us") i++;
  // "give my board a forest theme": the board is who it is for
  const who = thing(w, i);
  if (who.det === "def" && whole(who, boardSaid) && NEWLY.has(w[who.end] ?? "")) {
    const t = thing(w, who.end);
    const k = kindOf(t, w, false);
    return k === "theme" ? reads("theme") : k === "arrange" ? ARRANGE : ASK;
  }
  const t = who;
  if (t.det === "def" || t.pronoun) return undefined;
  if (!t.words.length) return undefined;
  const k = kindOf(t, w, creating);
  if (k === "board") return ASK;
  if (k === undefined) return undefined;
  // "add a title to my calendar", "add a red background to the clock": something for a widget that is there;
  // "add a clock next to my calendar" is a new one beside it
  if (!creating && k !== "theme" && k !== "arrange") {
    const c = clauseFrom(w, t.end, 6);
    const at = c.findIndex((x, j) => (x === "to" && c[j - 1] !== "next" && c[j - 1] !== "close") || x === "on" || x === "onto" || x === "into");
    if (at >= 0) {
      const target = thing(c, at + 1);
      if (target.det === "def" && (oneWidget(target) || (MANY.has(head(target)) && !BOARD.has(head(target))))) return ASK;
    }
  }
  return reads(k);
}

/** "make the board tidy", "make my timer bigger", "make my board look like a forest", "make the clock show seconds". */
function madeInto(w: string[], t: Thing, boardSaid: boolean | "one"): Read {
  const all = whole(t, boardSaid);
  const c = clauseFrom(w, t.end, 10);
  for (let k = 0; k < c.length; k++) {
    const x = c[k];
    if (SIZE.has(x) || (x === "size" && (c[k - 1] === "same" || c[k - 1] === "equal")) || (x === "width" && c[k - 1] === "full") || x === "fit") return ARRANGE;
    if ((x === "look" || x === "looks" || x === "feel" || x === "feels") && c[k + 1] === "like") return head(t) === "layout" ? ARRANGE : all ? reads("theme") : ASK;
    if (NEAT.has(x) || (x === "cluttered" || x === "messy" || x === "busy" || x === "crowded") || (x === "line" && c[k + 1] === "up") || (x === "in" && (c[k + 1] === "order" || c[k + 1] === "sections" || c[k + 1] === "rows" || c[k + 1] === "columns"))) return all ? ARRANGE : ASK;
    if ((x === "look" || x === "feel") && all) {
      // "make my board look clean and minimal": a look; "look tidy" was taken above
      const next = c[k + 1];
      if (next && !NEAT.has(next) && next !== "less" && next !== "more") return reads("theme");
    }
  }
  return ASK;
}

/** What one clause asks, read from its verb. Nothing when it says nothing the board can tell. */
function clause(w: string[], i: number, boardSaid: boolean | "one", first: boolean): Read | undefined {
  i = afterLeads(w, i);
  const v = w[i];
  if (v === undefined) return undefined;
  // "have my board in sections": the board as wished for, though "have" can start a question
  if (v === "have" && (DEFINITE.has(w[i + 1] ?? "") || PRONOUN.has(w[i + 1] ?? ""))) return wished(w, i + 1, boardSaid);
  if (QUESTION.has(v)) return first ? ASK : undefined;
  const wish = wishAt(w, i);
  if (wish && !wish.to) return wished(w, wish.at, boardSaid);
  if (wish) i = wish.at;
  const verb = verbAt(w, i);
  const next = w[i + 1];
  // a word that names a thing as well can start a description: "line chart of my revenue", "space theme";
  // or something said of one: "chart that never updates" is a chart, where "chart that for me" charts it
  const asVerb = !NOUNISH.has(verb) || next === undefined || (actsOn(next) && !aboutIt(w, i + 1));
  // "put together my widgets" groups the ones there; "whip up my notes" changes them; with something new, both make it
  if ((verb === "put" && next === "together") || (verb === "whip" && next === "up")) {
    const t = thing(w, i + 2);
    if (t.det === "def" || t.pronoun) return verb === "put" ? tidied(w, i, boardSaid) : ASK;
    return made(w, i + 2, true, boardSaid) ?? reads("widget");
  }
  if (verb === "set" && next === "up") return made(w, i + 2, true, boardSaid) ?? ASK;
  if (CREATE.has(verb) && asVerb) {
    const t = thing(w, i + 1 + (next === "me" || next === "us" ? 1 : 0));
    if (t.det === "def" || t.pronoun) {
      // "make the board tidy", "make me the board"... a thing that is there, changed
      if (verb === "make") return madeInto(w, t, boardSaid);
      return ASK;
    }
    return made(w, i + 1, true, boardSaid) ?? reads("widget");
  }
  if (verb === "give") {
    if (next === "me" || next === "us") return made(w, i + 2, false, boardSaid) ?? ASK;
    const who = thing(w, i + 1);
    const what = thing(w, who.end);
    if (whole(who, boardSaid) || who.pronoun === "it") {
      const k = kindOf(what, w, false);
      return k === "theme" ? reads("theme") : k === "arrange" ? ARRANGE : ASK;
    }
    return ASK;
  }
  if (verb === "show" || verb === "display") {
    const at = next === "me" || next === "us" ? i + 2 : i + 1;
    const t = thing(w, at);
    if (t.det === "new" && t.words.some((x) => WIDGET.has(x))) return reads("widget");
    if (asWidget(w, t.end)) return reads("widget");
    return ASK;
  }
  if (verb === "put" && next === "on") return made(w, i + 2, false, boardSaid) ?? ASK;
  // "see my widgets grouped by topic", "have my board in sections": the board as wished for
  if ((verb === "see" || verb === "have" || verb === "get") && (DEFINITE.has(next ?? "") || PRONOUN.has(next ?? ""))) return wished(w, i + 1, boardSaid);
  if (TIDY.has(verb) && asVerb) return tidied(w, i, boardSaid);
  // "keep the board tidy", "keep my widgets in order": the board as arranging leaves it
  if (verb === "keep") {
    const t = thing(w, i + 1);
    if ((t.det === "def" || t.pronoun) && madeInto(w, t, boardSaid) === ARRANGE) return ARRANGE;
  }
  if (MOVE.has(verb) && asVerb) {
    let j = i + 1;
    while (PARTICLE.has(w[j] ?? "")) j++;
    const t = thing(w, j);
    if (t.det === "new" || (t.det === null && !t.pronoun && t.words.length && (verb === "put" || verb === "place"))) {
      const k = PLACE.has(verb) ? kindOf(t, w, false) : undefined;
      if (k === "widget" || k === "theme" || k === "arrange") return reads(k);
      return ASK;
    }
    if (!t.words.length && !t.pronoun) return ASK;
    // a widget, or the board's widgets, moved into an order: "put my countdown timer at the top"
    const widgetish = t.pronoun || whole(t, boardSaid) || t.words.some((x) => WIDGET.has(x));
    return widgetish && whereTo(w, t.end) === "order" ? ARRANGE : ASK;
  }
  if (RESIZE.has(verb) && asVerb) {
    const t = thing(w, i + 1);
    return t.det === "def" || t.pronoun ? ARRANGE : ASK;
  }
  if (PLACE.has(verb) && asVerb) {
    const t = thing(w, i + 1);
    if (t.det === "def" || t.pronoun) return ASK;
    return made(w, i + 1, false, boardSaid) ?? ASK;
  }
  // "chart my weight"; not "chart not loading" or "chart totally broken", which say something of one
  if (PLOT.has(verb) && (asVerb || (next !== undefined && !ENDS.has(next) && !clauseFrom(w, i + 1).some((x) => FAULT.has(x)) && next !== ":"))) return reads("widget");
  if (verb === "display" && asVerb) return ASK;
  if ((verb === "switch" || verb === "use" || verb === "apply" || verb === "wear" || verb === "try" || verb === "set") && (next === "to" || next === "on" || NEWLY.has(next ?? ""))) {
    const t = thing(w, next === "to" || next === "on" ? i + 2 : i + 1);
    if (t.det === "new" && kindOf(t, w, false) === "theme") return reads("theme");
    return ASK;
  }
  // "fix the overlapping widgets": what arranging the board does
  if ((verb === "fix" || verb === "repair") && OVERLAP.test(clauseFrom(w, i).join(" "))) return ARRANGE;
  if (CHANGE.has(verb) && asVerb) return first ? ASK : undefined;
  if (!first) return undefined;
  return bare(w, i);
}

/** "I want a pomodoro timer", "I'd like my clock bigger", "I want my board organized by chart type". */
function wished(w: string[], i: number, boardSaid: boolean | "one"): Read {
  const t = thing(w, i);
  if (t.det === "def" || t.pronoun) {
    if (!whole(t, boardSaid)) return ASK;
    const c = clauseFrom(w, t.end, 10);
    for (let k = 0; k < c.length; k++) {
      const x = c[k];
      if ((x === "look" || x === "feel" || x === "looks" || x === "feels") && c[k + 1] === "like") return reads("theme");
      if (NEAT.has(x) || (x === "in" && (c[k + 1] === "order" || c[k + 1] === "sections"))) return ARRANGE;
    }
    return ASK;
  }
  const k = kindOf(t, w, false);
  return k === "widget" || k === "theme" || k === "arrange" ? reads(k) : ASK;
}

/** "tidy up the layout", "sort the table by date", "organize by type". */
function tidied(w: string[], i: number, boardSaid: boolean | "one"): Read {
  let j = i + 1;
  while (PARTICLE.has(w[j] ?? "")) j++;
  const t = thing(w, j);
  // "tidy up", "organize by type", "please organize", "organize all": the board, left unsaid
  if (!t.words.length && !t.pronoun) return t.det === null || w[j] === "all" ? ARRANGE : ASK;
  if (t.pronoun) return whole(t, boardSaid) ? ARRANGE : ASK;
  if (t.det === "new") {
    const k = kindOf(t, w, false);
    if (k === "widget" || k === "theme" || k === "arrange") return reads(k);
    return asWidget(w, t.end) ? reads("widget") : ASK;
  }
  if (whole(t, boardSaid)) return ARRANGE;
  // "organize my snake game and calendar": more than one widget
  if (WIDGET.has(head(t)) && w[t.end] === "and") {
    const more = thing(w, t.end + 1);
    if (WIDGET.has(head(more))) return ARRANGE;
  }
  // "lay out my week as a calendar widget"
  if (asWidget(w, t.end)) return reads("widget");
  return ASK;
}

/** A sentence that starts with a thing, not a verb: "a forest theme", "snake game", "one of my charts is broken". */
function bare(w: string[], i: number): Read | undefined {
  const a = w[i];
  // "one of my charts", "some of the widgets", "a lot of my widgets", "a few of my timers": ones that are there
  if ((QUANTITY.has(a) && w[i + 1] === "of") || (w[i + 2] === "of" && (w[i + 1] === "lot" || w[i + 1] === "few" || w[i + 1] === "couple" || w[i + 1] === "bunch"))) return undefined;
  if (DEFINITE.has(a) || PRONOUN.has(a) || a === "there" || a === "here" || a === "i" || a === "we" || a === "you" || a === "they") return undefined;
  if (a === "one" || /^\d/.test(a)) return undefined;
  // "theme the board by topic": a verb, though not one the board knows
  const n = w[i + 1] ?? "";
  if (n !== "that" && (DEFINITE.has(n) || PRONOUN.has(n) || n === "me" || n === "us")) return undefined;
  // a thank-you at the end is no part of it
  const body = w.slice(0, politeEnd(w));
  const t = thing(body, i);
  if (!t.words.length) return undefined;
  // "two clocks", "some charts": more than one widget, which says how many there are or asks for several, not one to make
  for (let j = i; j < body.length && (DEFINITE.has(body[j]) || NEWLY.has(body[j]) || COUNT.has(body[j])); j++) if (COUNT.has(body[j]) && MANY.has(head(t))) return undefined;
  const k = kindOf(t, body, false);
  if ((k !== "widget" && k !== "theme" && k !== "arrange") || !described(body, i, t, k)) return undefined;
  return k === "arrange" ? ARRANGE : reads(k, true);
}

/** Where a thank-you, and the marks around it, start at the end of the words: "a forest theme, please!". */
function politeEnd(w: string[]): number {
  let e = w.length;
  for (;;) {
    while (e > 0 && MARK.test(w[e - 1]) && w[e - 1] !== "?") e--;
    if (e > 0 && POLITE.has(w[e - 1])) e--;
    else if (e > 1 && w[e - 2] === "thank" && w[e - 1] === "you") e -= 2;
    else return e;
  }
}

/**
 * Whether a thing named with no verb before it describes one to make, and says nothing else: its name, with the
 * word for its kind last ("snake game", "a todo list app"), and then at most what it is like ("with neon colors",
 * "that shows my mrr", "for my team"). A look for a new widget names the widget after it: "a retro look for a
 * clock". Anything else said of it is a statement for the agent, whatever word it comes after: "snake game too
 * small", "a widget disappeared", "clock on my board is an hour behind", "a widget that never loads".
 */
function described(w: string[], i: number, t: Thing, k: Intent): boolean {
  const kind = k === "theme" ? (x: string) => THEME.has(x) || STYLE.has(x) : k === "arrange" ? (x: string) => LAYOUT.has(x) || ROOM.has(x) : (x: string) => WIDGET.has(x);
  let name = t;
  // "a retro look for a clock", "a dark background for a new clock": the look of a new widget
  if (k === "widget" && !t.words.some(kind) && STYLE.has(head(t)) && ["for", "to", "on", "of", "in", "onto", "into"].includes(w[t.end] ?? "")) {
    name = thing(w, t.end + 1);
    if (name.det !== "new") return false;
  }
  let at = -1;
  name.words.forEach((x, j) => kind(x) && (at = j));
  if (at < 0) return false;
  const after = name.words.slice(at + 1);
  if (after.length && !CONNECT.has(after[0]) && !(after.length === 1 && GENERIC.has(after[0]))) return false;
  const end = name.end;
  const next = w[end];
  if (next !== undefined && next !== "." && next !== "!" && next !== ":" && !CONNECT.has(next) && !SUBJECT.has(next) && !(next === "," && CONNECT.has(w[end + 1] ?? ""))) return false;
  // nothing before a widget's name says one is wrong: "broken clock", "frozen calendar" (a "frozen lake theme" is a look)
  if (k === "widget") for (let j = i; j < end; j++) if (FAULT.has(w[j])) return false;
  return !says(w, end, k === "widget", TIMELY.has(name.words[at]));
}

/** Where a piece of what a thing is like ends: "for bus stops near me" is bus stops, "for London shows Paris" is not. */
const ends = (n: string | undefined) => n === undefined || MARK.test(n) || CONNECT.has(n) || n === "and" || n === "or" || n === "but" || n === "plus" || n === "per";
/** Words that say when, how often or how a verb is done: "reset overnight", "stops randomly", "runs fast". */
const OFTEN = words("again overnight yesterday today twice sometimes randomly suddenly constantly fast late early");
/** How far a clock is off: "is five minutes fast", "runs an hour ahead"; but "a calendar that shows a week ahead". */
const UNIT = words("second seconds minute minutes hour hours day days week weeks");
const OFF_BY = words("fast slow behind ahead off");
const BEING = words("is are was were isn't it's seems looks runs running ran gets got");

/**
 * Whether what follows the name of a thing says something of one that is there, not what a new one is like. What
 * a new one is like comes in pieces, each after a word from CONNECT: "with a bell", "for London", "of broken
 * links", "showing the time in Tokyo", "that shows my github stars", "called snek". Said of one that is there:
 *  - a verb of the sentence's own, past where the thing is or what it is for: "clock on my board is an hour
 *    behind", "weather widget for London shows Paris", "chart for my revenue never updates";
 *  - what went wrong, in a clause of its own: "a widget that never loads", "clock that stopped", "a widget that I
 *    made", "a timer that resets itself";
 *  - for a widget, what it shows when it is wrong: "chart with no data", "widget with an error", "chart showing
 *    NaN", "news feed showing old news", "clock showing yesterday's date";
 *  - when it was made: "chart from yesterday".
 * A state after "of", "for" or "about" is what the widget lists: "a counter of broken links", "a list of tasks
 * that are stuck", "a timer for my breaks".
 */
function says(w: string[], from: number, widget: boolean, timely: boolean): boolean {
  // the word the piece being read starts with: "" right after the name, "for" in "for London", "that"
  let open = "";
  // in what the widget is about, after "of", "for" or "about"
  let content = false;
  // in a clause of its own, whose verbs are its own: "that shows my mrr", "when it ends", "things I have to do", "called snek"
  let own = false;
  for (let j = from; j < w.length; j++) {
    const x = w[j];
    const n = w[j + 1];
    // another sentence: anything wrong in it is still said of the thing
    if (x === "." || x === "!" || x === "?" || x === ";") return w.slice(j + 1).some((y) => FAULT.has(y));
    // a state is what the widget lists while it names them ("broken links", "projects behind schedule") or in a
    // clause about them ("tasks that are stuck"), not after them: "the chart for my revenue stopped"
    const listed = content && STATES.has(x) && (own || (!ends(n) && !OBJECT.has(n ?? "") && !/..ing$/.test(n ?? "")));
    // and "breaks" is a pause but where it is a verb: "a timer for my breaks", "a timer to count my breaks", not "a chart that breaks"
    const pause = x === "breaks" && !/^(?:that|which|who|it|they|often|always|sometimes|still|just|also)$|..ly$/.test(w[j - 1] ?? "");
    if (FAULT.has(x) && !listed && !pause) return true;
    if (widget && shownWrong(w, j, timely)) return true;
    if (x === "that" || x === "which" || x === "who" || x === "that's") {
      // what the thing did, or keeps doing, rather than what it does; a clause about what it lists can be past: "a list of tasks that I finished"
      if (!content && x !== "that's" && didWrong(w, j + 1)) return true;
      open = x;
      own = true;
      continue;
    }
    if (UNIT.has(x) && OFF_BY.has(n ?? "") && offBy(w, j)) return true;
    if (SUBJECT.has(x)) {
      // "a widget I made" is one that is there, "a theme I saw on dribbble" one to make
      if (open === "" && didWrong(w, j)) return true;
      own = true;
      continue;
    }
    if (x === "whose" || x === "where" || x === "when" || x === "while" || x === "if" || x === "because" || x === "until" || x === "till" || x === "what" || x === "whatever" || x === "how" || x === "why") {
      own = true;
      continue;
    }
    if (CONNECT.has(x)) {
      // "the chart from yesterday", "widget from earlier": one made then
      if (open === "" && x === "from" && madeThen(w, j + 1)) return true;
      open = x;
      content = CONTENT.has(x);
      // what it is called or says, and what it is for: "a widget to help me keep track", not "a countdown to my launch"
      own ||= x === "called" || x === "named" || x === "titled" || x === "saying" || (x === "to" && n !== undefined && !DEFINITE.has(n) && !NEWLY.has(n) && !/^\d/.test(n));
      continue;
    }
    if (own) continue;
    if (FINITE.has(x)) return true;
    if (DOES.has(x) && !ends(n)) return true;
    if (DOES_TOO.has(x) && n !== undefined && (OBJECT.has(n) || PARTICLE.has(n) || OFTEN.has(n) || /..(ing|ly)$/.test(n))) return true;
    // "kanban on my board deleted my cards", not "a list of deleted files"
    if (x.length > 3 && /[^e]ed$/.test(x) && OBJECT.has(n ?? "")) return true;
  }
  return false;
}

/** Whether a clause of a thing's own says what it did, or keeps doing: "clock that stopped", "a widget that I made", "a chart that used to work", "a widget that keeps reloading", "a timer that resets itself". */
function didWrong(w: string[], j: number): boolean {
  const who = SUBJECT.has(w[j] ?? "");
  if (who) j++;
  while (/..ly$/.test(w[j] ?? "") || w[j] === "just" || w[j] === "also" || w[j] === "already" || w[j] === "still") j++;
  const v = w[j] ?? "";
  const n = w[j + 1] ?? "";
  // "a widget that I made", "the chart I added": what a user did to one on the board
  if (who) return BOARD_ACTS.has(v);
  // "a widget that errors", "a widget that times out", "a chart that logs me out"
  if (v === "errors" || ((v === "times" || v === "logs") && (n === "out" || w[j + 2] === "out"))) return true;
  if (v === "was" || v === "were" || v === "wasn't" || v === "weren't" || v === "had" || v === "hadn't" || v === "did" || v === "didn't" || v === "used") return true;
  if (WENT.has(v) || (v.length > 3 && /[^e]ed$/.test(v))) return true;
  if ((v === "keeps" || v === "keep" || v === "kept") && n.endsWith("ing")) return true;
  // "resets itself", "closes on its own"; "a counter that resets itself every day" does it when asked
  if (UNASKED.has(v)) {
    const k = n === "itself" ? j + 2 : n === "by" && w[j + 2] === "itself" ? j + 3 : n === "on" && w[j + 2] === "its" && w[j + 3] === "own" ? j + 4 : -1;
    if (k > 0 && !SCHEDULE.has(w[k] ?? "")) return true;
  }
  return false;
}

/** Whether a widget is said to show what one shows when something is wrong: "chart with no data", "widget with an error", "chart showing zero", "news feed showing old news", "clock showing yesterday's date". */
function shownWrong(w: string[], j: number, timely: boolean): boolean {
  const x = w[j];
  let k = j + 1;
  if (x === "without") {
    while (w[k] === "any" || w[k] === "the" || w[k] === "its") k++;
    return CONTENTS.has(w[k] ?? "");
  }
  if (!SHOWS.has(x)) return false;
  // "a calendar showing last week", but "a calendar showing the last 30 days"
  if (timely && PAST_DAY.has(w[k] ?? "") && x !== "with" && x !== "has" && x !== "had" && x !== "having") return true;
  while (w[k] === "a" || w[k] === "an" || w[k] === "the" || w[k] === "just" || w[k] === "only") k++;
  const y = w[k] ?? "";
  const n = w[k + 1];
  // "with no data", but "a clock with no numbers" and "a news feed with no ads" are what they are like
  if (y === "no") return CONTENTS.has(n ?? "");
  // "with an error", "showing zero", "with an error message", but "a chart showing error rates" and "a timer with zero distractions"
  if (WRONG.has(y)) return ends(n) || n === "message" || n === "messages" || n === "everywhere" || n === "again" || n === "instead";
  // "showing old news", "a feed that shows old news", but "a frame with old photos"
  return y === "old" && x !== "with" && x !== "has" && x !== "had" && x !== "having";
}

/** Whether the time unit at j is how far the thing is off: "a clock that is five minutes fast", "that runs an hour ahead". */
function offBy(w: string[], j: number): boolean {
  let b = j - 1;
  while (b >= 0 && (NEWLY.has(w[b]) || /^\d/.test(w[b]) || w[b] === "few" || w[b] === "couple" || w[b] === "of")) b--;
  return b < j - 1 && BEING.has(w[b] ?? "");
}

/** Whether the words from j are only a time: "yesterday", "earlier", "last week", "a few days ago". */
function madeThen(w: string[], j: number): boolean {
  let k = j;
  while (k < w.length && WHEN_MADE.has(w[k])) k++;
  return k > j && ends(w[k]) && w.slice(j, k).some((x) => x !== "a" && x !== "an" && x !== "the" && x !== "of" && x !== "few" && x !== "couple");
}


/** "my board needs a tidy", "my layout could be neater", "keep my widgets in order", "the widgets overlap". */
const BOARD_THEN_TIDY =
  /\b(?:board|layout|dashboard|canvas|workspace|widgets|tiles|cards|charts|everything|it|this)\s(?:(?:look|looking|be|get|stay|feel|could be|should be|more|much|a bit|a little|nice and|neat and)\s){0,4}(?:tidy|tidier|neat|neater|organi[sz]ed|orderly|decluttered|uncluttered|clean|cleaner|less (?:cluttered|messy|busy|crowded)|more (?:organi[sz]ed|orderly)|sorted|grouped|arranged|in (?:order|sections|rows|columns|groups|a grid))\b/;
const BOARD_NEEDS =
  /\b(?:board|layout|dashboard|canvas|workspace|widgets|everything)\s(?:needs?|could use|is due|wants)\s(?:a\s(?:good\s)?)?(?:tidy|tidying|clean ?up|cleaning|de-?clutter(?:ing)?|re-?organi[sz](?:ing|ation)|organi[sz](?:ing|ation)|sort(?:ing)?|re-?arrang(?:ing|ement)|re-?shuffle)\b/;
const OVERLAP = /\b(?:widgets|tiles|cards|charts|things)\s(?:\w+\s){0,2}(?:overlap|collid)|\b(?:overlapping|colliding)\s(?:widgets|tiles|cards|charts)\b/;

/** The words of a text as read: lowercased, with where each one starts in the text. */
function wordsOf(text: string): { w: string[]; at: number[] } {
  const w: string[] = [];
  const at: number[] = [];
  for (const m of text.slice(0, READ).matchAll(WORD)) {
    w.push(m[0].toLowerCase().replace(/’/g, "'"));
    at.push(m.index);
  }
  return { w, at };
}

/** The first clause that says what it asks decides; a statement can be followed by a request, or say the board needs a tidy. */
function read(text: string, w: string[]): Read {
  if (!/\S\s+\S/.test(text.slice(0, READ))) return { intent: null, bare: false };
  const sentence: Intent = /\S\s+\S+\s+\S/.test(text.slice(0, READ)) || text.endsWith("?") ? "ask" : null;
  // what "it" is: the board, unless the sentence names one widget and not the board
  const boardSaid: boolean | "one" = w.some((x) => BOARD_SAID.has(x)) ? true : w.some((x) => WIDGET.has(x) && !MANY.has(x)) ? "one" : false;
  const said = (r: Read): Read => (r.intent === "ask" ? { intent: sentence, bare: false } : r);
  const first = clause(w, 0, boardSaid, true);
  if (first) return said(first);
  // a statement, then a request: "my board is a mess, organize it"
  for (let k = 0, n = 0; k < w.length && n < 8; k++) {
    if (!(w[k] === "," || w[k] === "." || w[k] === ";" || w[k] === "!" || w[k] === "?" || w[k] === "and" || w[k] === "but" || w[k] === "so" || w[k] === "then")) continue;
    n++;
    const r = clause(w, k + 1, boardSaid, false);
    if (r?.intent === "arrange") return r;
  }
  const flat = w.join(" ");
  if (BOARD_THEN_TIDY.test(flat) || BOARD_NEEDS.test(flat) || OVERLAP.test(flat)) return ARRANGE;
  return { intent: sentence, bare: false };
}

export interface Request {
  intent: Intent;
  /** A description with no verb of its own ("snake game", "a forest theme"): "Make a widget:" or "Make a theme:" goes in front of it. */
  bare: boolean;
  /**
   * The words to hand over. A bare description, without "a theme like" in front; a widget or theme
   * asked for with a verb, as typed from that verb on, past "hey, can you"; anything else, as typed.
   */
  words: string;
}

/** What a sentence typed into the bar asks for, and the words that ask it. */
export function requestOf(raw: string): Request {
  const text = raw.trim();
  const { w, at } = wordsOf(text);
  const r = read(text, w);
  if (r.intent !== "widget" && r.intent !== "theme") return { ...r, words: text };
  if (r.bare) return { ...r, words: ideaOf(text) };
  const k = afterLeads(w, 0, capsOf(text, at));
  return { ...r, words: k > 0 && k < w.length ? text.slice(at[k]) : text };
}

/** Which words were typed with a capital: "Hello Kitty", "Hey Arnold". */
const capsOf = (text: string, at: number[]) => at.map((p) => /\p{Lu}/u.test(text[p]));

/** What a sentence typed into the bar is asking for, if it is a sentence at all. */
export function intentOf(raw: string): Intent {
  const text = raw.trim();
  return read(text, wordsOf(text).w).intent;
}

/** "a widget that groups my tasks" is "one that groups my tasks"; "a theme like a harbor" is "a harbor". */
const KIND_FIRST = /^(?:(?:a|an|the)\s+)?(?:new\s+)?(?:custom\s+)?(?:theme|look|widget|tile)(?:\s*:\s*|\s+)(?:(?:that|which)\s+(?:shows?|looks? like|feels? like)\s+|(?:showing|like|of|for|with|about)\s+|(?<one>(?:that|which)\s+))?/i;

/** Words that what follows them is what a thing says or is called: "says thanks", "a sign reading please". */
const SAYS = /\b(?:say|says|saying|said|read|reads|reading|show|shows|showing|display|displays|displaying|write|writes|writing|print|prints|spell|spells|call|called|name|named|title|titled|label|labeled|labelled|word|words|text)\s*$/i;

/** The thing asked for, without "hey, can you make me a widget that shows" in front of it. */
export function ideaOf(raw: string): string {
  const t = raw.trim();
  const { w, at } = wordsOf(t);
  const k = afterLeads(w, 0, capsOf(t, at));
  const from = k > 0 && k < w.length ? t.slice(at[k]) : t;
  let out = from
    // a verb up front: "make me a widget that shows..."
    .replace(/^(make|build|create|design|draw|add|give)\s+(?=(?:me|a|an|the|new|custom|theme|look|widget|tile|chart)\b)(me\s+)?((a|an|the)\s+)?(new\s+)?(custom\s+)?((theme|look|widget|tile|chart)\s+)?((that|which)\s+)?((shows?|showing|like|of|for|with|about)\s+)?/i, "")
    .trim();
  // or the kind of thing up front: "a theme like...", "a widget that shows...", "new theme: misty mountains"
  const kind = KIND_FIRST.exec(out);
  if (kind) out = ((kind.groups?.one ? "one that " : "") + out.slice(kind[0].length)).trim();
  // "a forest theme please", but not what the thing says: "a widget that says thanks"
  const tail = /(?:^|[\s,])(?:please|pls|thanks|thank you)[.!]*$/i.exec(out.slice(-24));
  if (tail && !SAYS.test(out.slice(0, out.length - tail[0].length))) out = out.slice(0, out.length - tail[0].length).replace(/[\s,;:]+$/, "");
  return out || t;
}
